import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import getBITSEndApiMap from "../apiMap";
import { api } from "../services/AxiosService";

export interface Student {
  id: string;
  name: string;
  email: string;
}

export interface Reviewer {
  id: string;
  name: string;
  email: string;
}

export interface Course {
  id: string;
  name: string;
}

export interface Project {
  id: string;
  title: string;
  course_id: string;
  course: Course;
}

export interface SandboxSession {
  id: string;
  user_id: string;
  project_id: string;
  startTime: string;
  endTime: string;
  duration: number;
  last_heartbeat: string;
  status: string;
  extension_requested: boolean;
  createdAt: string;
  updatedAt: string;
  project: Project;
}

export interface TimeExtensionRequest {
  id: string;
  sandbox_session_id: string;
  student_id: string;
  reason: string;
  duration_value: string;
  status: "pending" | "approved" | "rejected" | "expired";
  reviewed_by: string | null;
  reviewed_at: string | null;
  review_comments: string | null;
  approved_due_date: string | null;
  created_at: string;
  updated_at: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  student: Student;
  reviewer: Reviewer | null;
  sandboxSession: SandboxSession;
}

export interface FilterProject {
  id: string;
  title: string;
  course_id: string;
}

export interface FilterCourse {
  course_id: string;
  name: string;
}

export interface TimeExtensionFilters {
  projects: FilterProject[];
  courses: FilterCourse[];
}

export interface TimeExtensionData {
  rows: TimeExtensionRequest[];
  count: number;
  page: number;
  totalPages: number;
  filters: TimeExtensionFilters;
}

export interface GetTimeExtensionRequestsResponse {
  isSuccess: boolean;
  message: string;
  data: TimeExtensionData;
}

// API Functions
export const getTimeExtensionRequests = async (params?: {
  status?: string;
  courseId?: string;
  projectId?: string;
  priority?: string;
  search?: string;
  page?: number;
  limit?: number;
}): Promise<GetTimeExtensionRequestsResponse> => {
  const url = getBITSEndApiMap("getTimeExtensionRequests");
  const { data } = await api.get<GetTimeExtensionRequestsResponse>(url, {
    params,
  });
  return data;
};

// React Query Hooks
export const useTimeExtensionRequests = (
  params?: {
    status?: string;
    courseId?: string;
    projectId?: string;
    priority?: string;
    search?: string;
    page?: number;
    limit?: number;
  },
  enabled: boolean = true,
) => {
  return useQuery({
    queryKey: ["timeExtensionRequests", params],
    queryFn: () => getTimeExtensionRequests(params),
    staleTime: 30000, // 30 seconds
    enabled,
  });
};

// Update time extension request status
export interface UpdateTimeExtensionStatusPayload {
  status: "approved" | "rejected";
  duration_value: string; // Format: "HHH:MM" e.g., "090:00"
  review_comments: string;
}

export interface UpdateTimeExtensionStatusResponse {
  isSuccess: boolean;
  message: string;
  data: TimeExtensionRequest;
}

export const updateTimeExtensionStatus = async (
  id: string,
  payload: UpdateTimeExtensionStatusPayload,
): Promise<UpdateTimeExtensionStatusResponse> => {
  const url = getBITSEndApiMap("updateTimeExtensionStatus").replace(":id", id);
  const { data } = await api.put<UpdateTimeExtensionStatusResponse>(
    url,
    payload,
  );
  return data;
};

// React Query Mutation Hook
export const useUpdateTimeExtensionStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: UpdateTimeExtensionStatusPayload;
    }) => updateTimeExtensionStatus(id, payload),
    onSuccess: () => {
      // Invalidate and refetch both pending and history queries
      queryClient.invalidateQueries({ queryKey: ["timeExtensionRequests"] });
    },
  });
};

// Bulk update time extension request status
export interface BulkUpdateTimeExtensionStatusPayload {
  ids: string[];
  status: "approved" | "rejected";
  review_comments: string;
  duration_value: string; // Format: "HHH:MM" e.g., "090:00"
}

export interface BulkUpdateTimeExtensionStatusResponse {
  isSuccess: boolean;
  message: string;
  data: {
    updated: number;
    failed: number;
  };
}

export const bulkUpdateTimeExtensionStatus = async (
  payload: BulkUpdateTimeExtensionStatusPayload,
): Promise<BulkUpdateTimeExtensionStatusResponse> => {
  const url = getBITSEndApiMap("bulkUpdateTimeExtensionStatus");
  const { data } = await api.put<BulkUpdateTimeExtensionStatusResponse>(
    url,
    payload,
  );
  return data;
};

// React Query Mutation Hook for Bulk Update
export const useBulkUpdateTimeExtensionStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: BulkUpdateTimeExtensionStatusPayload) =>
      bulkUpdateTimeExtensionStatus(payload),
    onSuccess: () => {
      // Invalidate and refetch both pending and history queries
      queryClient.invalidateQueries({ queryKey: ["timeExtensionRequests"] });
    },
  });
};
